// Design System EmptyState Component
// Unified component for displaying when there's no data

import React from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  useTheme, 
  alpha, 
  Paper,
  styled
} from '@mui/material';
import { 
  AutoAwesome as MagicIcon,
  Inbox as DefaultIcon
} from '@mui/icons-material';

// Import design tokens
import { spacingTokens, colorUtils } from '../../tokens';

// Styled components using design tokens
const StyledContainer = styled(Paper)(({ theme, variant, magical, showBorder, borderStyle }) => {
  const mode = theme.palette.mode;
  const colors = theme.palette;
  
  // Base styles
  const baseStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    borderRadius: spacingTokens.component.lg,
    position: 'relative',
    overflow: magical ? 'hidden' : 'visible',
    transition: 'all 0.3s ease',
  };
  
  // Variant-specific styles
  const variantStyles = {
    default: {
      backgroundColor: mode === 'dark' 
        ? alpha(colors.background.paper, 0.4) 
        : alpha(colors.background.paper, 0.8),
      border: showBorder ? `1px ${borderStyle} ${alpha(colors.divider, 0.6)}` : 'none',
      padding: spacingTokens.layout.md,
    },
    compact: {
      backgroundColor: alpha(colors.background.paper, 0.3),
      border: showBorder ? `1px ${borderStyle} ${alpha(colors.divider, 0.4)}` : 'none',
      padding: spacingTokens.layout.sm,
      borderRadius: spacingTokens.component.md,
    },
    subtle: {
      backgroundColor: 'transparent',
      border: showBorder ? `1px ${borderStyle} ${alpha(colors.divider, 0.3)}` : 'none',
      padding: spacingTokens.layout.md,
    },
    highlight: {
      backgroundColor: alpha(colors.primary.main, 0.05),
      border: showBorder ? `1px ${borderStyle} ${alpha(colors.primary.main, 0.2)}` : 'none',
      padding: spacingTokens.layout.lg,
    },
  };
  
  // Magical effects
  const magicalStyles = magical ? {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, 
        transparent 0%,
        ${alpha(colors.primary.main, 0.05)} 50%,
        transparent 100%)`,
      animation: 'magicalShimmer 3s infinite ease-in-out',
      zIndex: 0,
    },
    '@keyframes magicalShimmer': {
      '0%, 100%': {
        opacity: 0.3,
      },
      '50%': {
        opacity: 0.7,
      },
    },
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
  } : {};
  
  return {
    ...baseStyles,
    ...variantStyles[variant],
    ...magicalStyles,
  };
});

const StyledIconContainer = styled(Box)(({ theme }) => ({
  marginBottom: spacingTokens.layout.sm,
  color: theme.palette.text.secondary,
  opacity: 0.7,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '& svg': {
    fontSize: '4rem',
    transition: 'transform 0.3s ease',
  },
  '&:hover svg': {
    transform: 'scale(1.05)',
  },
}));

const StyledMagicIcon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: spacingTokens.component.md,
  right: spacingTokens.component.md,
  color: theme.palette.primary.main,
  animation: 'sparkle 2s infinite ease-in-out',
  zIndex: 2,
  '@keyframes sparkle': {
    '0%, 100%': {
      opacity: 0.5,
      transform: 'scale(1) rotate(0deg)',
    },
    '50%': {
      opacity: 1,
      transform: 'scale(1.15) rotate(10deg)',
    },
  },
}));

/**
 * EmptyState - Unified component for displaying when there's no data
 * 
 * Features:
 * - Multiple visual variants
 * - Magical effects support
 * - Filter-aware messaging
 * - Consistent with design system
 * - Responsive design
 * - Accessibility compliant
 */
const EmptyState = ({ 
  // Content
  title = 'No items found',
  description = 'There are no items to display.',
  icon,
  action,
  
  // Style variants
  variant = 'default',
  magical = false,
  size = 'medium', // 'small', 'medium', 'large'
  
  // Filtering context
  filtered = false,
  getFilteredTitle,
  getFilteredDescription,
  
  // Style customization
  showBorder = true,
  borderStyle = 'dashed',
  
  // Layout
  minHeight,
  maxWidth = '100%',
  
  // Other props
  sx = {},
  ...rest 
}) => {
  const theme = useTheme();
  
  // Determine title and description based on filtering
  const displayTitle = filtered && getFilteredTitle 
    ? getFilteredTitle() 
    : title;
    
  const displayDescription = filtered && getFilteredDescription
    ? getFilteredDescription()
    : description;
  
  // Size-based configurations
  const sizeConfig = {
    small: {
      minHeight: minHeight || 200,
      iconSize: '3rem',
      titleVariant: 'h6',
      padding: spacingTokens.layout.sm,
    },
    medium: {
      minHeight: minHeight || 300,
      iconSize: '4rem',
      titleVariant: 'h5',
      padding: spacingTokens.layout.md,
    },
    large: {
      minHeight: minHeight || 400,
      iconSize: '5rem',
      titleVariant: 'h4',
      padding: spacingTokens.layout.lg,
    },
  };
  
  const config = sizeConfig[size] || sizeConfig.medium;
  
  // Default icon if none provided
  const displayIcon = icon || <DefaultIcon />;
  
  return (
    <StyledContainer
      elevation={0}
      variant={variant}
      magical={magical}
      showBorder={showBorder}
      borderStyle={borderStyle}
      sx={{
        minHeight: config.minHeight,
        maxWidth,
        mx: 'auto',
        ...sx
      }}
      role="status"
      aria-label={`${displayTitle}: ${displayDescription}`}
      {...rest}
    >
      {/* Magical sparkle indicator */}
      {magical && (
        <StyledMagicIcon>
          <MagicIcon />
        </StyledMagicIcon>
      )}
      
      {/* Icon */}
      <StyledIconContainer
        sx={{
          '& svg': {
            fontSize: config.iconSize,
          }
        }}
      >
        {displayIcon}
      </StyledIconContainer>
      
      {/* Title */}
      <Typography
        variant={config.titleVariant}
        component="h2"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          mb: spacingTokens.component.sm,
          px: spacingTokens.component.md,
          textAlign: 'center',
        }}
      >
        {displayTitle}
      </Typography>
      
      {/* Description */}
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          mb: action ? spacingTokens.layout.sm : 0,
          px: spacingTokens.component.md,
          textAlign: 'center',
          lineHeight: 1.6,
        }}
      >
        {displayDescription}
      </Typography>
      
      {/* Action button or element */}
      {action && (
        <Box 
          sx={{ 
            mt: spacingTokens.layout.sm,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: spacingTokens.component.sm,
          }}
        >
          {action}
        </Box>
      )}
    </StyledContainer>
  );
};

EmptyState.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  icon: PropTypes.node,
  action: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'compact', 'subtle', 'highlight']),
  magical: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  filtered: PropTypes.bool,
  getFilteredTitle: PropTypes.func,
  getFilteredDescription: PropTypes.func,
  showBorder: PropTypes.bool,
  borderStyle: PropTypes.oneOf(['dashed', 'solid', 'none']),
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object,
};

export default EmptyState;